import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, Input, ViewEncapsulation, HostBinding, Directive, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i1 from '@windmill/ng-windmill/shared';

/**
 * To use this component in a module, you can import the `CentricComplexFormModule`:
 * @example
 * ```typescript
 * import { CentricComplexFormModule } from '@windmill/ng-windmill/complex-form';
 * ```
 *
 * To use the standalone component directly, you can import `FormControlComponent`:
 * @example
 * ```typescript
 * import { FormControlComponent } from '@windmill/ng-windmill/complex-form';
 * ```
 */
class FormControlComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormControlComponent, isStandalone: true, selector: "centric-form-control", ngImport: i0, template: "<ng-content></ng-content>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormControlComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-form-control', imports: [CommonModule], standalone: true, template: "<ng-content></ng-content>\r\n" }]
        }] });

/**
 * To use this component in a module, you can import the `CentricComplexFormModule`:
 * @example
 * ```typescript
 * import { CentricComplexFormModule } from '@windmill/ng-windmill/complex-form';
 * ```
 *
 * To use the standalone component directly, you can import `FormItemGroupComponent`:
 * @example
 * ```typescript
 * import { FormItemGroupComponent } from '@windmill/ng-windmill/complex-form';
 * ```
 */
class FormItemGroupComponent {
    /**
     * Layout type for component
     */
    type = 'block';
    /**
     * Items alignment when type is inline
     */
    alignItems = 'top';
    /**
     * Width for component
     */
    width = '100%';
    /**
     * Whether the component has left label grey background
     */
    hasLeftLabelGreyBackground = false;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormItemGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormItemGroupComponent, isStandalone: true, selector: "centric-form-item-group", inputs: { type: "type", alignItems: "alignItems", width: "width", hasLeftLabelGreyBackground: "hasLeftLabelGreyBackground" }, ngImport: i0, template: "<div\r\n  class=\"centric-form-item-group\"\r\n  [class.form-item-group-inline]=\"type === 'inline'\"\r\n  [class.form-item-group-block]=\"type === 'block'\"\r\n  [class.align-items-end]=\"alignItems === 'bottom'\"\r\n  [class.has-left-label-grey-background]=\"hasLeftLabelGreyBackground\"\r\n  [style.width]=\"width\">\r\n  <ng-content></ng-content>\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormItemGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-form-item-group', encapsulation: ViewEncapsulation.None, imports: [CommonModule], standalone: true, template: "<div\r\n  class=\"centric-form-item-group\"\r\n  [class.form-item-group-inline]=\"type === 'inline'\"\r\n  [class.form-item-group-block]=\"type === 'block'\"\r\n  [class.align-items-end]=\"alignItems === 'bottom'\"\r\n  [class.has-left-label-grey-background]=\"hasLeftLabelGreyBackground\"\r\n  [style.width]=\"width\">\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], propDecorators: { type: [{
                type: Input
            }], alignItems: [{
                type: Input
            }], width: [{
                type: Input
            }], hasLeftLabelGreyBackground: [{
                type: Input
            }] } });

/**
 * To use this component in a module, you can import the `CentricComplexFormModule`:
 * @example
 * ```typescript
 * import { CentricComplexFormModule } from '@windmill/ng-windmill/complex-form';
 * ```
 *
 * To use the standalone component directly, you can import `FormItemComponent`:
 * @example
 * ```typescript
 * import { FormItemComponent } from '@windmill/ng-windmill/complex-form';
 * ```
 */
class FormItemComponent {
    /**
     * Width for component
     */
    get width() {
        return this._customWidth;
    }
    set width(newWidth) {
        this._customWidth = newWidth;
        this.flexBasis = newWidth;
    }
    flexBasis;
    _customWidth = '100%';
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormItemComponent, isStandalone: true, selector: "centric-form-item", inputs: { width: "width" }, host: { properties: { "style.width": "this.width", "style.flex-basis": "this.flexBasis" } }, ngImport: i0, template: "<ng-content></ng-content>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-form-item', encapsulation: ViewEncapsulation.None, imports: [CommonModule], standalone: true, template: "<ng-content></ng-content>\r\n" }]
        }], propDecorators: { width: [{
                type: HostBinding,
                args: ['style.width']
            }, {
                type: Input
            }], flexBasis: [{
                type: HostBinding,
                args: ['style.flex-basis']
            }] } });

/**
 * To use this component in a module, you can import the `CentricComplexFormModule`:
 * @example
 * ```typescript
 * import { CentricComplexFormModule } from '@windmill/ng-windmill/complex-form';
 * ```
 *
 * To use the standalone component directly, you can import `FormLabelComponent`:
 * @example
 * ```typescript
 * import { FormLabelComponent } from '@windmill/ng-windmill/complex-form';
 * ```
 */
class FormLabelComponent {
    /**
     * Specifies which form element a label is bound to
     */
    attrFor;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: FormLabelComponent, isStandalone: true, selector: "centric-form-label", inputs: { attrFor: "attrFor" }, ngImport: i0, template: "<label [attr.for]=\"attrFor\">\r\n  <ng-content></ng-content>\r\n</label>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-form-label', imports: [CommonModule], standalone: true, template: "<label [attr.for]=\"attrFor\">\r\n  <ng-content></ng-content>\r\n</label>\r\n" }]
        }], propDecorators: { attrFor: [{
                type: Input
            }] } });

const FORM_CONTROL_SIZE = {
    selectionWithoutLabel: 16,
    extrasmallOrSelectionWithLabel: 24,
    small: 32
};
const SMALL_HEIGHT_CLASS = 'height-small';
const EXTRASMALL_HEIGHT_CLASS = 'height-extrasmall';
const WITHOUT_LABEL_HEIGHT_CLASS = 'height-selection-control-without-label';
/**
 * To use this component in a module, you can import the `CentricComplexFormModule`:
 * @example
 * ```typescript
 * import { CentricComplexFormModule } from '@windmill/ng-windmill/complex-form';
 * ```
 *
 * To use the standalone component directly, you can import `FormDirective`:
 * @example
 * ```typescript
 * import { FormDirective } from '@windmill/ng-windmill/complex-form';
 * ```
 */
class FormDirective {
    elementRef;
    responsiveService;
    /**
     * Position for label
     */
    labelPosition = 'top';
    /**
     * Length for label
     */
    labelLength = 'medium';
    /**
     * Alignment of label
     */
    labelAlignment = 'right';
    formClasses;
    initialFormClasses = [];
    formItemElements;
    resizeObserverFormItems;
    resizeObserverForm;
    isSmallBreakpoint = false;
    isSmallBreakpointPreviousValue = false;
    constructor(elementRef, responsiveService) {
        this.elementRef = elementRef;
        this.responsiveService = responsiveService;
    }
    ngOnChanges() {
        this.updateFormClass();
    }
    ngOnInit() {
        this.updateFormClass(true);
    }
    ngAfterViewInit() {
        this.formItemElements = this.getArrayOfCentricFormItems();
        if (this.labelPosition === 'left') {
            this.observeResizeFormItems();
            this.observeResizeForm();
        }
    }
    ngAfterViewChecked() {
        const newFormItemElements = this.getArrayOfCentricFormItems();
        if (!this.compareArrays(this.formItemElements, newFormItemElements)) {
            this.formItemElements = newFormItemElements;
            this.disconnectResizeObserver();
            if (this.labelPosition === 'left') {
                this.observeResizeFormItems();
                this.observeResizeForm();
            }
        }
    }
    ngOnDestroy() {
        this.disconnectResizeObserver();
    }
    updateFormClass(isOnInit) {
        let newFormClasses = ['centric-complex-form'];
        if (isOnInit) {
            const formClassListValue = this.elementRef.nativeElement.classList.value;
            this.initialFormClasses = formClassListValue ? formClassListValue.split(' ') : null;
        }
        newFormClasses = this.initialFormClasses ? newFormClasses.concat(this.initialFormClasses) : newFormClasses;
        newFormClasses = newFormClasses.concat(this.updateFormLabelClass());
        this.formClasses = newFormClasses.join(' ');
    }
    updateFormLabelClass() {
        const newClasses = [];
        if (this.labelPosition === 'top') {
            newClasses.push(this.labelPosition);
        }
        else {
            const labelAlignmentClassName = `label-${this.labelAlignment}`;
            if (this.isSmallBreakpoint) {
                newClasses.push(this.labelPosition, 'small', labelAlignmentClassName);
            }
            else {
                newClasses.push(this.labelPosition, this.labelLength, labelAlignmentClassName);
            }
        }
        return newClasses;
    }
    observeResizeFormItems() {
        this.resizeObserverFormItems = new ResizeObserver(() => {
            this.verticallyAlignFormItemLabels();
        });
        this.formItemElements.forEach(formItem => {
            this.resizeObserverFormItems.observe(formItem);
        });
    }
    observeResizeForm() {
        this.resizeObserverForm = new ResizeObserver(() => {
            this.isSmallBreakpoint = this.responsiveService.isSmall(this.elementRef.nativeElement.clientWidth);
            if (this.isSmallBreakpoint !== this.isSmallBreakpointPreviousValue) {
                this.updateFormClass();
                this.isSmallBreakpointPreviousValue = this.isSmallBreakpoint;
            }
        });
        this.resizeObserverForm.observe(this.elementRef.nativeElement);
    }
    verticallyAlignFormItemLabels() {
        this.formItemElements.forEach(element => {
            const formControlElement = element.lastElementChild?.firstElementChild?.firstElementChild;
            if (formControlElement) {
                this.resetClasses(element);
                this.addClasses(element, formControlElement);
                this.handleTextarea(element, formControlElement);
            }
        });
    }
    resetClasses(element) {
        element.classList.remove(SMALL_HEIGHT_CLASS, EXTRASMALL_HEIGHT_CLASS, WITHOUT_LABEL_HEIGHT_CLASS);
        if (element.classList.value === '') {
            element.removeAttribute('class');
        }
    }
    addClasses(element, formControlElement) {
        switch (formControlElement?.clientHeight) {
            case FORM_CONTROL_SIZE.small:
                element.classList.add(SMALL_HEIGHT_CLASS);
                break;
            case FORM_CONTROL_SIZE.extrasmallOrSelectionWithLabel:
                element.classList.add(EXTRASMALL_HEIGHT_CLASS);
                break;
            case FORM_CONTROL_SIZE.selectionWithoutLabel:
                element.classList.add(WITHOUT_LABEL_HEIGHT_CLASS);
                break;
            default:
                break;
        }
    }
    handleTextarea(element, formControlElement) {
        const textareaElement = formControlElement?.classList.contains('centric-textarea') ? formControlElement : null;
        if (textareaElement) {
            const textareaClassList = textareaElement.classList;
            if (textareaClassList.contains('small')) {
                element.classList.add(SMALL_HEIGHT_CLASS);
            }
            else if (textareaClassList.contains('extrasmall')) {
                element.classList.add(EXTRASMALL_HEIGHT_CLASS);
            }
        }
    }
    disconnectResizeObserver() {
        if (this.resizeObserverFormItems) {
            this.resizeObserverFormItems.disconnect();
        }
        if (this.resizeObserverForm) {
            this.resizeObserverForm.disconnect();
        }
    }
    getArrayOfCentricFormItems() {
        return Array.from(this.elementRef.nativeElement.getElementsByTagName('centric-form-item'));
    }
    compareArrays(firstArray, secondArray) {
        return firstArray.length === secondArray.length && firstArray.every((element, index) => element === secondArray[index]);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormDirective, deps: [{ token: i0.ElementRef }, { token: i1.ResponsiveService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: FormDirective, isStandalone: true, selector: "[centric-complex-form]", inputs: { labelPosition: "labelPosition", labelLength: "labelLength", labelAlignment: "labelAlignment" }, host: { properties: { "class": "this.formClasses" } }, exportAs: ["centricComplexForm"], usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: FormDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[centric-complex-form]',
                    exportAs: 'centricComplexForm',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.ResponsiveService }], propDecorators: { labelPosition: [{
                type: Input
            }], labelLength: [{
                type: Input
            }], labelAlignment: [{
                type: Input
            }], formClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

const moduleComponents = [FormControlComponent, FormDirective, FormItemComponent, FormItemGroupComponent, FormLabelComponent];
class CentricComplexFormModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricComplexFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricComplexFormModule, imports: [CommonModule, FormControlComponent, FormDirective, FormItemComponent, FormItemGroupComponent, FormLabelComponent], exports: [FormControlComponent, FormDirective, FormItemComponent, FormItemGroupComponent, FormLabelComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricComplexFormModule, imports: [CommonModule, FormControlComponent, FormItemComponent, FormItemGroupComponent, FormLabelComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricComplexFormModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricComplexFormModule, FormControlComponent, FormDirective, FormItemComponent, FormItemGroupComponent, FormLabelComponent };
